import { useCallback, useEffect, useRef } from 'react'
import { getProjectDocumentContent } from '@/lib/api/v2/projects'

/** How many documents stay in memory: the one shown, the prefetched next one and a few to go back to. */
const MAX_CACHED = 6

/**
 * SOLY-DOC-002 — fetches documents' bytes and hands out object URLs for the viewer. A small LRU
 * (moving back and forth stays instant) whose evicted URLs are revoked, so walking through 30
 * large files never keeps them all in the tab; everything is revoked on unmount.
 */
export function useDocumentBlobUrls(projectId: string | undefined) {
  const cache = useRef(new Map<string, Promise<string>>())

  useEffect(() => {
    const entries = cache.current
    return () => {
      for (const pending of entries.values()) revokeLater(pending)
      entries.clear()
    }
  }, [projectId])

  const getUrl = useCallback(
    (documentId: string): Promise<string> => {
      if (!projectId) return Promise.reject(new Error('No project'))
      const entries = cache.current
      const cached = entries.get(documentId)
      if (cached) {
        // Refresh its rank: the most recently used goes last.
        entries.delete(documentId)
        entries.set(documentId, cached)
        return cached
      }
      const pending = getProjectDocumentContent(projectId, documentId).then((blob) => URL.createObjectURL(blob))
      // A failed fetch must not stay cached: the next attempt retries.
      pending.catch(() => entries.delete(documentId))
      entries.set(documentId, pending)
      while (entries.size > MAX_CACHED) {
        const [oldestId, oldest] = entries.entries().next().value as [string, Promise<string>]
        entries.delete(oldestId)
        revokeLater(oldest)
      }
      return pending
    },
    [projectId]
  )

  return { getUrl }
}

function revokeLater(pending: Promise<string>) {
  pending.then((url) => URL.revokeObjectURL(url)).catch(() => undefined)
}
